import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";

// Connects to data-controller="product-and-variant-selector"
export default class extends Controller {
  static outlets = ["polaris-autocomplete"];
  static targets = [
    "productGidField",
    "variantGidField",
    "variantGidWrapper",
    "variantGidPlaceholderField",
  ];
  static values = { shopId: String };

  productGidFieldTargetConnected(target) {
    target.addEventListener(
      "polaris-autocomplete:change",
      this.onProductChanged,
    );

    this.productAutocompleteOutlet.inputTarget.addEventListener(
      "input",
      this.onProductInputChanged,
    );

    this.productAutocompleteOutlet.inputTarget.addEventListener(
      "blur",
      this.onProductInputBlurred,
    );
  }

  productGidFieldTargetDisconnected(target) {
    target.removeEventListener(
      "polaris-autocomplete:change",
      this.onProductChanged,
    );
    this.productAutocompleteOutlet.inputTarget.removeEventListener(
      "input",
      this.onProductInputChanged,
    );
    this.productAutocompleteOutlet.inputTarget.removeEventListener(
      "blur",
      this.onProductInputBlurred,
    );
  }

  variantGidFieldTargetConnected() {
    const outlet = this.variantAutocompleteOutlet;

    if (!outlet) {
      return;
    }

    this.hideVariantGidPlaceholderField();
    this.showVariantGidField();

    outlet.inputTarget.addEventListener("blur", this.onVariantInputBlurred);
  }

  variantGidFieldTargetDisconnected() {
    const outlet = this.variantAutocompleteOutlet;

    if (!outlet) {
      return;
    }

    outlet.inputTarget.removeEventListener("blur", this.onVariantInputBlurred);
  }

  onProductChanged = (eventData) => {
    this.resetVariantGidField();
    this.showVariantGidPlaceholderField();
    this.hideVariantGidField();

    const productId = eventData.detail.value;
    this.fetchVariantsFromProduct(productId);
  };

  onProductInputChanged = (event) => {
    if (event.target.value) {
      return;
    }

    this.resetProductGidField();
    this.hideVariantGidField();
    this.resetVariantGidField();
    this.showVariantGidPlaceholderField();
  };

  hideVariantGidPlaceholderField() {
    if (!this.variantGidPlaceholderFieldTarget.classList.contains("hidden")) {
      this.variantGidPlaceholderFieldTarget.classList.add("hidden");
    }
  }

  showVariantGidPlaceholderField() {
    if (this.variantGidPlaceholderFieldTarget.classList.contains("hidden")) {
      this.variantGidPlaceholderFieldTarget.classList.remove("hidden");
    }
  }

  hideVariantGidField() {
    if (!this.variantGidWrapperTarget.classList.contains("hidden")) {
      this.variantGidWrapperTarget.classList.add("hidden");
    }
  }

  showVariantGidField() {
    if (this.variantGidWrapperTarget.classList.contains("hidden")) {
      this.variantGidWrapperTarget.classList.remove("hidden");
    }
  }

  resetVariantGidField() {
    const variantAutocompleteOutlet = this.variantAutocompleteOutlet;
    if (variantAutocompleteOutlet) {
      if (variantAutocompleteOutlet.hasHiddenInputTarget) {
        variantAutocompleteOutlet.hiddenInputTarget.value = "";
      }
      if (variantAutocompleteOutlet.hasInputTarget) {
        variantAutocompleteOutlet.inputTarget.value = "";
      }

      variantAutocompleteOutlet.reset();
    }
  }

  resetProductGidField() {
    const productAutocompleteOutlet = this.productAutocompleteOutlet;
    if (productAutocompleteOutlet) {
      if (productAutocompleteOutlet.hasHiddenInputTarget) {
        productAutocompleteOutlet.hiddenInputTarget.value = "";
      }
      if (productAutocompleteOutlet.hasInputTarget) {
        productAutocompleteOutlet.inputTarget.value = "";
      }

      productAutocompleteOutlet.reset();
    }
  }

  onProductInputBlurred = (event) => {
    const productAutocompleteOutlet = this.productAutocompleteOutlet;

    const selectedValue = productAutocompleteOutlet.hiddenInputTarget.value;

    const selectedOption = productAutocompleteOutlet.visibleOptions.find(
      (option) => {
        const input = option.querySelector("input");

        return input.value === selectedValue;
      },
    );

    if (selectedOption) {
      if (event.target.value === selectedOption.dataset.label) {
        return;
      }

      event.target.value = selectedOption.dataset.label;
    }
  };

  onVariantInputBlurred = (event) => {
    const variantAutocompleteOutlet = this.variantAutocompleteOutlet;

    const selectedValue = variantAutocompleteOutlet.hiddenInputTarget.value;

    const selectedOption = variantAutocompleteOutlet.visibleOptions.find(
      (option) => {
        const input = option.querySelector("input");

        return input.value === selectedValue;
      },
    );

    if (selectedOption) {
      if (event.target.value === selectedOption.dataset.label) {
        return;
      }

      event.target.value = selectedOption.dataset.label;
    }
  };

  fetchVariantsFromProduct(productId) {
    const target = this.variantGidWrapperTarget.id;
    get(
      "/retailer/shopify/variants" +
        `?target=${target}` +
        `&product_id=${productId}` +
        `&shop_id=${this.shopIdValue}`,
      { responseKind: "turbo-stream" },
    );
  }

  get productAutocompleteOutlet() {
    return this.polarisAutocompleteOutlets.find(
      (outlet) => outlet.element.id === "product-gid-autocomplete",
    );
  }

  get variantAutocompleteOutlet() {
    return this.polarisAutocompleteOutlets.find(
      (outlet) => outlet.element.id === "variant-gid-autocomplete",
    );
  }
}
